import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import ordenDevolucionService, {
  OrdenDevolucion,
  RemitoPendienteDevolucion,
} from '../../services/ordenDevolucionService';
import { getApiErrorMessage } from '../../utils/apiError';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';

const formatFecha = (value?: string | Date | null) => {
  if (!value) return '-';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-AR');
};

const formatCantidad = (n: number) =>
  new Intl.NumberFormat('es-AR', { maximumFractionDigits: 4, minimumFractionDigits: 0 }).format(n);

const formatComprobante = (letra?: string | null, nroSuc?: number, nroComprobante?: number | null) => {
  const L = (letra || 'X').trim() || 'X';
  const suc = String(nroSuc ?? 0).padStart(5, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

type TabMode = 'ordenes' | 'pendientes';

const Devoluciones: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();

  const [mode, setMode] = useState<TabMode>('ordenes');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [ordenes, setOrdenes] = useState<OrdenDevolucion[]>([]);
  const [pendientes, setPendientes] = useState<RemitoPendienteDevolucion[]>([]);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [ordenDetalle, setOrdenDetalle] = useState<OrdenDevolucion | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm.trim() || undefined,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        nroSuc: resolveNroSucFiltro(filtroNroSuc),
      };

      if (mode === 'ordenes') {
        const result = await ordenDevolucionService.getOrdenes(params);
        setOrdenes(result.items || []);
        setTotalCount(result.totalCount || 0);
      } else {
        const result = await ordenDevolucionService.getRemitosPendientes(params);
        setPendientes(result.items || []);
        setTotalCount(result.totalCount || 0);
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar órdenes de devolución'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [
    enqueueSnackbar,
    fechaDesde,
    fechaHasta,
    filtroNroSuc,
    mode,
    page,
    rowsPerPage,
    searchTerm,
  ]);

  useEffect(() => {
    if (!filtroListo) return;
    void loadData();
  }, [loadData, filtroListo]);

  const handleVerDetalle = async (id: number) => {
    try {
      const orden = await ordenDevolucionService.getOrden(id);
      setOrdenDetalle(orden);
      setDetalleOpen(true);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo cargar el detalle'), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Órdenes de Devolución
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/ventas/ordenes-devolucion/nuevo')}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Nueva Orden
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant={mode === 'ordenes' ? 'contained' : 'outlined'}
              onClick={() => {
                setMode('ordenes');
                setPage(0);
              }}
              sx={{ textTransform: 'none' }}
            >
              Órdenes
            </Button>
            <Button
              variant={mode === 'pendientes' ? 'contained' : 'outlined'}
              onClick={() => {
                setMode('pendientes');
                setPage(0);
              }}
              sx={{ textTransform: 'none' }}
            >
              Remitos pendientes
            </Button>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por número, cliente..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fechaDesde}
                onChange={(e) => {
                  setFechaDesde(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fechaHasta}
                onChange={(e) => {
                  setFechaHasta(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FiltroSucursalVentaSelect
                value={filtroNroSuc}
                onChange={(v) => {
                  setFiltroNroSuc(v);
                  setPage(0);
                }}
                sucursales={sucursales}
                loading={loadingSucursales}
              />
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : mode === 'ordenes' ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Remito origen</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordenes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No hay órdenes de devolución
                      </TableCell>
                    </TableRow>
                  ) : (
                    ordenes.map((o) => (
                      <TableRow key={o.nroFormulario} hover>
                        <TableCell>
                          {formatComprobante(o.letra, o.nroSuc, o.nroComprobante)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            #{o.nroFormulario}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatFecha(o.fechaEmision)}</TableCell>
                        <TableCell>
                          {o.nroCliente} - {o.nombre || ''}
                        </TableCell>
                        <TableCell>{o.nroFormularioRemito || o.nroRemito || '-'}</TableCell>
                        <TableCell>{o.asentado === 'A' ? 'Anulada' : 'Activa'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Ver detalle">
                            <IconButton size="small" onClick={() => void handleVerDetalle(o.nroFormulario)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Remito</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Renglones</TableCell>
                    <TableCell align="right">Pendiente</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendientes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No hay remitos con cantidades pendientes de devolver
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendientes.map((p) => (
                      <TableRow key={p.nroFormularioRemito} hover>
                        <TableCell>
                          {formatComprobante(p.letra, p.nroSuc, p.nroComprobante)}
                          <Typography variant="caption" display="block" color="text.secondary">
                            #{p.nroFormularioRemito}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatFecha(p.fechaEmision)}</TableCell>
                        <TableCell>
                          {p.nroCliente} - {p.nombre || ''}
                        </TableCell>
                        <TableCell align="right">{p.renglonesConPendiente}</TableCell>
                        <TableCell align="right">{formatCantidad(p.totalPendienteDevolver)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Devolver">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                navigate(`/ventas/ordenes-devolucion/nuevo?remitoId=${p.nroFormularioRemito}`)
                              }
                            >
                              <AssignmentReturnIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage="Filas"
          />
        </CardContent>
      </Card>

      <Dialog open={detalleOpen} onClose={() => setDetalleOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Orden{' '}
          {ordenDetalle
            ? formatComprobante(ordenDetalle.letra, ordenDetalle.nroSuc, ordenDetalle.nroComprobante)
            : ''}
        </DialogTitle>
        <DialogContent dividers>
          {ordenDetalle && (
            <Stack spacing={2}>
              <Typography>
                Cliente: {ordenDetalle.nroCliente} - {ordenDetalle.nombre}
              </Typography>
              <Typography>Remito origen: #{ordenDetalle.nroFormularioRemito}</Typography>
              <Typography>Fecha: {formatFecha(ordenDetalle.fechaEmision)}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Artículo</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(ordenDetalle.detalles || []).map((d) => (
                    <TableRow key={d.nroItem}>
                      <TableCell>{d.codigoArticulo}</TableCell>
                      <TableCell>{d.descripcionArticulo}</TableCell>
                      <TableCell align="right">{formatCantidad(Number(d.cantidad) || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalleOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Devoluciones;
